import { formatDistanceToNow } from "date-fns";
import { RotateCcw, Sparkles } from "lucide-react";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { FileIcon } from "@components/ui/file-icon";

import { FileWithIndexing } from "../../../../types/file-indexing";

type AiAnswerUnavailableProps = {
  /** Files stuck at indexing_status=FAILED for this case. */
  failedFiles: FileWithIndexing[];
  /** Total number of documents on the case (indexed + failed + pending). */
  totalCount: number;
  /** Whether a re-index request is in flight. */
  isReindexing: boolean;
  onReindex: () => void;
};

/**
 * Shown when one or more case documents couldn't be prepared for search, so an
 * AI answer would miss their evidence. Offers a one-click retry to prepare the
 * affected documents and re-run the search.
 *
 * Copy note: the underlying operation is search indexing, but we surface it as
 * "preparing documents" throughout — the medical-expert audience shouldn't need
 * to know the internal term.
 */
export const AiAnswerUnavailable = ({
  failedFiles,
  totalCount,
  isReindexing,
  onReindex,
}: AiAnswerUnavailableProps) => {
  const failedCount = failedFiles.length;

  // "Last tried N ago" — the most recent prep timestamp across the affected
  // files. Absent on backends that don't stamp indexed_at, in which case we omit
  // the line rather than show a misleading "just now".
  const lastAttempt = failedFiles
    .map((f) => f.indexed_at)
    .filter((d): d is string => Boolean(d))
    .sort()
    .at(-1);

  const lastAttemptLabel = lastAttempt
    ? `Last tried ${formatDistanceToNow(new Date(lastAttempt), {
        addSuffix: true,
      })}`
    : null;

  return (
    <div
      className="m-4 rounded-lg border border-dashed border-primary-200 bg-primary-50/40 p-4"
      data-testid="ai-answer-unavailable"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="size-[18px] shrink-0 text-secondary" />
        <span className="font-head text-[13px] font-semibold text-foreground">
          AI Answer needs all documents ready
        </span>
      </div>

      <p className="mt-2 text-xs leading-[1.65] text-foreground-muted">
        We couldn&apos;t prepare{" "}
        <span className="font-semibold text-foreground">
          {failedCount} of {totalCount}
        </span>{" "}
        {totalCount === 1 ? "document" : "documents"} for search, so this answer
        might miss some detail. Try again to search across every document.
      </p>

      <ul className="mt-3 flex flex-col items-start gap-1.5">
        {failedFiles.map((file) => (
          <li
            key={file.id}
            className="flex max-w-full items-center gap-2 rounded-md bg-destructive-50 px-2.5 py-1.5 text-[11px] font-medium text-destructive-700 ring-1 ring-inset ring-destructive-100"
          >
            <FileIcon fileName={file.name} className="size-3.5 shrink-0" />
            <span className="min-w-0 truncate">{file.name}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <button
          type="button"
          onClick={onReindex}
          disabled={isReindexing}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isReindexing ? (
            <LoadingIcon className="size-4 animate-spin-ccw" />
          ) : (
            <RotateCcw className="size-4" />
          )}
          {isReindexing ? "Preparing documents…" : "Prepare & search again"}
        </button>
        {lastAttemptLabel && (
          <span className="text-xs text-foreground-muted">{lastAttemptLabel}</span>
        )}
      </div>
    </div>
  );
};
